// Home cards for in-salon add-ons: running-late requests still waiting for the customer's yes (the desk can
// stop waiting), and the in-salon upsell report per stylist and branch (credited to who sold it, with export).
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api, { addDays, formatDateTime, formatTime, isoDate } from '../services/api';
import { useEngagementScope } from '../context/EngagementScope';
import { DownloadIcon, exportCSV } from './ui';

export function WaitingForReplyCard() {
  const { ready, me } = useEngagementScope();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () =>
    api
      .listAddonRequests('waiting')
      .then((d) => setRows(d.requests || []))
      .catch(() => setRows([]));
  useEffect(() => {
    if (!ready || !me) return undefined;
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [ready, me]);
  if (!rows || !rows.length) return null;
  const stop = async (id) => {
    setBusy(id);
    try {
      await api.cancelAddonRequest(id);
      toast.success('Stopped waiting. Their times stay as they were.');
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="de-card de-mt-16" style={{ borderLeft: '4px solid var(--de-warn)' }}>
      <div className="de-card-title">
        ⏳ Waiting for reply <span className="de-pill de-pill--warn de-pill--sm">{rows.length}</span>
      </div>
      <div className="de-small de-muted" style={{ marginBottom: 8 }}>
        A service was added in the salon. These customers were asked on WhatsApp to come one slot later; nothing moves until they say yes.
      </div>
      {rows.map((r) => (
        <div key={r.id} className="de-line-item">
          <div style={{ minWidth: 0 }}>
            <div className="de-strong">{r.service || 'Extra slot'} for booking #{r.booking_id}</div>
            {r.changes.map((c) => (
              <div key={c.appointment_id} className="de-small">
                {c.customer_name} ({c.service}) → {formatTime(c.new_start)}:{' '}
                <span className={c.status === 'accepted' ? 'de-ok-text' : 'de-warn-text'}>{c.status === 'accepted' ? 'said yes' : 'no answer yet'}</span>
              </div>
            ))}
            {r.expires_at ? <div className="de-small de-muted">The desk is alerted at {formatDateTime(r.expires_at)} if nobody answers.</div> : null}
          </div>
          <button type="button" className="de-btn de-btn--outline de-btn--xs" disabled={busy === r.id} onClick={() => stop(r.id)}>
            Stop waiting
          </button>
        </div>
      ))}
    </div>
  );
}

const CSV = [
  { label: 'Stylist', key: 'name' },
  { label: 'Services added', key: 'count' },
  { label: 'Revenue (₹)', key: 'revenue' },
  { label: 'Visits', key: 'visits' },
  { label: 'Average per visit (₹)', key: 'average_per_visit' },
];

export function UpsellReportCard() {
  const { ready, me } = useEngagementScope();
  const [from, setFrom] = useState(addDays(isoDate(), -30));
  const [to, setTo] = useState(isoDate());
  const [data, setData] = useState(null);
  const [view, setView] = useState('by_stylist');
  useEffect(() => {
    if (!ready || !me) return undefined;
    api
      .upsellReport({ date_from: from, date_to: to })
      .then(setData)
      .catch(() => setData({ by_stylist: [], by_branch: [] }));
  }, [ready, me, from, to]);
  const rows = data ? data[view] || [] : [];
  return (
    <div className="de-card de-mt-16">
      <div className="de-row" style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div className="de-card-title" style={{ margin: 0 }}>
          In-salon upsells
        </div>
        <div className="de-row" style={{ gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" className={`de-chip${view === 'by_stylist' ? ' active' : ''}`} onClick={() => setView('by_stylist')}>
            By stylist
          </button>
          <button type="button" className={`de-chip${view === 'by_branch' ? ' active' : ''}`} onClick={() => setView('by_branch')}>
            By branch
          </button>
          <input type="date" className="de-input de-input--bordered" style={{ width: 150 }} value={from} onChange={(e) => setFrom(e.target.value)} />
          <input type="date" className="de-input de-input--bordered" style={{ width: 150 }} value={to} onChange={(e) => setTo(e.target.value)} />
          <button
            type="button"
            className="de-btn de-btn--outline de-btn--sm"
            disabled={!rows.length}
            onClick={() => exportCSV(rows, [{ ...CSV[0], label: view === 'by_branch' ? 'Branch' : 'Stylist' }, ...CSV.slice(1)], `in_salon_upsells_${view}`)}
          >
            <DownloadIcon /> Excel
          </button>
        </div>
      </div>
      <div className="de-small de-muted" style={{ margin: '6px 0 8px' }}>
        Services added during a visit and taken, credited to the stylist who sold them.
      </div>
      {!data ? null : rows.length ? (
        <div className="de-table-wrap">
          <table className="de-table">
            <thead>
              <tr>
                <th>{view === 'by_branch' ? 'Branch' : 'Stylist'}</th>
                <th>Services added</th>
                <th>Revenue</th>
                <th>Visits</th>
                <th>Average per visit</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={String(r.id)}>
                  <td className="de-td-strong">{r.name}</td>
                  <td>{r.count}</td>
                  <td>₹{Number(r.revenue).toLocaleString('en-IN')}</td>
                  <td>{r.visits}</td>
                  <td>₹{Number(r.average_per_visit).toLocaleString('en-IN')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="de-small de-muted">No services added in the salon in these dates.</div>
      )}
    </div>
  );
}

const OFFER_KIND = { upsell: 'Upsell (pairs well)', downsell: 'Downsell (cheaper option)' };

/** Upsell and downsell messages: sent, booked from them, taken and revenue (credited per booked line). */
export function OfferReportCard() {
  const { ready, me } = useEngagementScope();
  const [from, setFrom] = useState(addDays(isoDate(), -30));
  const [to, setTo] = useState(isoDate());
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!ready || !me) return undefined;
    api
      .offerReport({ date_from: from, date_to: to })
      .then(setData)
      .catch(() => setData(null));
  }, [ready, me, from, to]);
  if (!data) return null;
  const kinds = Object.entries(data.kinds || {});
  return (
    <div className="de-card de-mt-16">
      <div className="de-row" style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div className="de-card-title" style={{ margin: 0 }}>
          Offer messages
        </div>
        <div className="de-row" style={{ gap: 6 }}>
          <input type="date" className="de-input de-input--bordered" style={{ width: 150 }} value={from} onChange={(e) => setFrom(e.target.value)} />
          <input type="date" className="de-input de-input--bordered" style={{ width: 150 }} value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
      </div>
      <div className="de-small de-muted" style={{ margin: '6px 0 8px' }}>
        Upsell and downsell messages from the offer rules, and the bookings made from them.
      </div>
      <div className="de-table-wrap">
        <table className="de-table">
          <thead>
            <tr>
              <th>Message</th>
              <th>Sent</th>
              <th>Booked from it</th>
              <th>Taken</th>
              <th>Revenue</th>
              <th>Top service</th>
            </tr>
          </thead>
          <tbody>
            {kinds.map(([kind, r]) => (
              <tr key={kind}>
                <td className="de-td-strong">{OFFER_KIND[kind] || kind}</td>
                <td>{r.sent}</td>
                <td>
                  {r.booked}
                  {r.sent ? <span className="de-small de-muted"> ({Math.round((r.booked / r.sent) * 100)}%)</span> : null}
                </td>
                <td>{r.taken}</td>
                <td>₹{Number(r.revenue).toLocaleString('en-IN')}</td>
                <td className="de-small">{r.services?.[0]?.service || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
